import React, {
  DragEvent,
  memo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import Spinner from 'pages/Dashboard/components/Spinner';
import Dropzone, {
  DropzoneHandle,
} from 'pages/Dashboard/components/Dropzone';
import type {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import type {
  Accept,
} from 'react-dropzone';
import {
  toLocalUrls,
} from 'utils/files';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useMixpanelTrack,
} from 'core/hooks';
import isNil from 'lodash/isNil';

type Props = {
  onUpload: (files: FileLocalUrl[]) => Promise<void>;
  accept?: Accept;
  disabled?: boolean;
  isUploading?: boolean;
};

function MediaUploadDropzone({
  onUpload,
  accept,
  disabled = false,
  isUploading = false,
}: Props) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dropzoneRef = useRef<DropzoneHandle | null>(null);
  const mixpanelTrack = useMixpanelTrack();

  const isMenuOpen = Boolean(menuAnchor);

  const restorePointerEvents = () => {
    if (!isNil(overlayRef.current)) {
      overlayRef.current.style.pointerEvents = 'auto';
    }
  };

  const handleDrop = async (acceptedFiles: File[]) => {
    try {
      if (acceptedFiles.length > 0) {
        const filesWithUrl = await toLocalUrls(acceptedFiles);
        await onUpload(filesWithUrl);
      }
    } finally {
      // Always restore pointer events after a drop, even if onUpload throws
      restorePointerEvents();
    }
  };

  // Disable overlay pointer events so drag events reach the underlying Dropzone
  const handleDragEnter = () => {
    if (!isNil(overlayRef.current)) {
      overlayRef.current.style.pointerEvents = 'none';
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    const leftContainer = isNil(e.relatedTarget)
      || !containerRef.current?.contains(e.relatedTarget as Node);
    if (leftContainer) {
      restorePointerEvents();
    }
  };

  const handleDragDrop = () => { restorePointerEvents(); };

  const handleDragEnd = () => { restorePointerEvents(); };

  const openMenu = () => {
    if (!disabled && !isNil(containerRef.current)) {
      setMenuAnchor(containerRef.current);
    }
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const handleTakePhoto = () => {
    closeMenu();
    mixpanelTrack('onStartWebcamScanFromVisitNote');
    mediaEventChannel.emit('onStartWebcamScan', {
      captureType: 'subjective-camera-capture',
      limit: 10,
      multiple: true,
      onCapturedFilesConfirmed: async (files: FileLocalUrl[]) => {
        await onUpload(files);
      },
    });
  };

  const handleBrowseFiles = () => {
    closeMenu();
    dropzoneRef.current?.open();
  };

  return (
    <>
      <div
        ref={containerRef}
        className="relative group"
        onDragOver={(e) => e.stopPropagation()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDragDrop}
        onDragEnd={handleDragEnd}
        style={{ width: 170, height: 70 }}
      >
        <Dropzone
          openRef={dropzoneRef}
          onDrop={handleDrop}
          accept={accept}
          multiple
          classes={{
            root: clsx(
              '!w-[170px] !h-[70px] !p-0 !border-dotted rounded-md transition-colors',
              { 'group-hover:bg-blue-100': !disabled && !isUploading },
              { '!cursor-not-allowed': disabled || isUploading },
            ),
            text: 'text-blue-500 text-xs font-semibold text-center px-2 leading-tight',
          }}
          placeholder={isUploading ? 'Uploading...' : 'Drop or Select Media'}
          icon={isUploading ? <Spinner size={16} className="!text-blue-500" /> : undefined}
          disabled={disabled || isUploading}
        />
        <div
          ref={overlayRef}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          aria-label="Choose upload source"
          className={clsx(
            'absolute inset-0 rounded-md',
            { 'cursor-pointer': !disabled && !isUploading },
            { 'pointer-events-none !cursor-not-allowed': disabled || isUploading },
          )}
          onClick={openMenu}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              openMenu();
            }
          }}
        />
      </div>

      {/* Menu opens ABOVE the card */}
      <Menu
        anchorEl={menuAnchor}
        open={isMenuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            className: '!rounded-lg !min-w-[150px] !shadow-lg !border !border-gray-100 !-translate-y-1.5',
          },
        }}
      >
        <MenuItem
          onClick={handleTakePhoto}
          className="!gap-2 !py-2 !px-3 hover:!bg-blue-50"
        >
          <CameraAltOutlinedIcon className="!w-4 !h-4 !text-gray-600" />
          <span className="text-xs font-medium text-gray-800">Capture from Webcam</span>
        </MenuItem>
        <div className="h-px bg-gray-100" />
        <MenuItem
          onClick={handleBrowseFiles}
          className="!gap-2 !py-2 !px-3 hover:!bg-blue-50"
        >
          <FolderOpenOutlinedIcon className="!w-4 !h-4 !text-gray-600" />
          <span className="text-xs font-medium text-gray-800">Browse Files</span>
        </MenuItem>
      </Menu>
    </>
  );
}

export default memo(MediaUploadDropzone);
